import gsap from 'gsap/all';
import * as PIXI from 'pixi.js';
import { EVENTS } from './EVENTS';

export * from "pixi.js";

export class Resources
{
    static textures: Record<string, PIXI.Texture> = {};
    static animations: Record<string, Array<PIXI.Texture>> = {};
    static sounds: Record<string, string> = {}
}


const sounds = [
    "mp3"
];


export interface IApplicationOptions extends PIXI.IApplicationOptions
{
    _resizeTo: HTMLElement;
    resources: Array<string>;
    onLoad: () => void;
    onLoadProgress?: (loader: PIXI.Loader, Resource: PIXI.Resource) => void;
    onLoadScope: any;
}


export class Application extends PIXI.Application
{


    onLoad: () => void;
    onLoadScope: any;
    loader: PIXI.Loader;
    
    flagResize: boolean;
    _timeResized: number;
    _resizeTo: HTMLElement;
    protected _width: number;
    protected _height: number;
    resolution: number;
    
    loadingGraphics: PIXI.Graphics;
    loadingGraphicsConfig = {
        bars: 15,
        visibleBars: 10,
        d1: 30,
        d2: 60,
        rotateDelay: .05,
        round_outer: 4,
        round_inner: 2
    }


    constructor(options: IApplicationOptions)
    {
        super(options);

        this.resolution = options.resolution || 1;

        this.rotateLoadingGraphics = this.rotateLoadingGraphics.bind(this);
        this.createLoadingScreen();
        
        this.onLoad = options.onLoad;
        this.onLoadScope = options.onLoadScope;

        this.loader = new PIXI.Loader();

        this.loader.pre(function(res, next){
            res.url += "?version=" + version;
            next();
        });

        options.resources.forEach(path => {
            const match = (/(.+\/)?((.+)(?:\.(\w+)))/g).exec(path);
            let name = "";
            if (match)
            {
                name = match[3];
            }
            
            if (sounds.indexOf(match[4]) !== -1)
            {
                this.loader.add(name, path, {
                    loadType: PIXI.LoaderResource.LOAD_TYPE.XHR,
                    xhrType: PIXI.LoaderResource.XHR_RESPONSE_TYPE.BLOB
                });
            }
            else
            {
                this.loader.add(name, path);
            }
        });
        
        if (options.onLoadProgress)
        {
            this.loader.onProgress.add(options.onLoadProgress.bind(options.onLoadScope));
        }
        
        this.loader.load(this.onLoadComplete.bind(this));

        this._resizeTo = options._resizeTo;
        this.flagResize = false;
        this._width = 0;
        this._height = 0;
        this.onResize = this.onResize.bind(this);
        window.addEventListener("resize", this.onResize);
        this.onResize();
    }


    get width()
    {
        return this._width;
    }


    get height()
    {
        return this._height;
    }


    onResize()
    {
        this._timeResized = performance.now();
        PIXI.Ticker.shared.remove(this.resizeListener, this);
        PIXI.Ticker.shared.add(this.resizeListener, this);
    }


    resizeListener()
    {
        if (this.width !== this._resizeTo.clientWidth || this.height !== this._resizeTo.clientHeight)
        {
            this.flagResize = true;
            this._width = this._resizeTo.clientWidth;
            this._height = this._resizeTo.clientHeight;
            this._timeResized = performance.now();
        }
        else if (performance.now() - this._timeResized > 250)
        {
            PIXI.Ticker.shared.remove(this.resizeListener, this);
        }
    }


    render()
    {
        if (this.flagResize)
        {
            EVENTS.emit(EVENTS.PIXI.RESIZE, this.width, this.height);
            this.renderer.resize(this.width, this.height);
            this.view.width = this.width * this.resolution;
            this.view.height = this.height * this.resolution;
            this.flagResize = false;
        }
        super.render();
    }


    createLoadingScreen()
    {
        this.loadingGraphics = new PIXI.Graphics();

        const pad1 = Math.PI * 2 / this.loadingGraphicsConfig.bars;
        const pad2 = pad1 * .5;

        const d1 = this.loadingGraphicsConfig.d1;
        const d2 = this.loadingGraphicsConfig.d2;

        const round_outer = this.loadingGraphicsConfig.round_outer;
        const round_inner = this.loadingGraphicsConfig.round_inner;

        for (let i = 0; i < this.loadingGraphicsConfig.visibleBars; i++)
        {
            const r1 = pad1 * i;
            const r2 = r1 + pad2;

            this.loadingGraphics.beginFill(0xffffff, (i + 1) / this.loadingGraphicsConfig.visibleBars);

            this.loadingGraphics.moveTo(
                Math.cos(r1) * (d1 + round_inner),
                Math.sin(r1) * (d1 + round_inner)
            );

            this.loadingGraphics.lineTo(
                Math.cos(r1) * (d2 - round_outer),
                Math.sin(r1) * (d2 - round_outer)
            );


            this.loadingGraphics.bezierCurveTo(
                Math.cos(r1) * (d2 - round_outer),
                Math.sin(r1) * (d2 - round_outer),
                
                Math.cos(r1) * (d2),
                Math.sin(r1) * (d2),
                
                Math.cos(r1) * (d2) + Math.cos(r1 + Math.PI * .5) * round_outer,
                Math.sin(r1) * (d2) + Math.sin(r1 + Math.PI * .5) * round_outer
            );


            this.loadingGraphics.lineTo(
                Math.cos(r2) * (d2) + Math.cos(r2 - Math.PI * .5) * round_outer,
                Math.sin(r2) * (d2) + Math.sin(r2 - Math.PI * .5) * round_outer
            );


            this.loadingGraphics.bezierCurveTo(
                Math.cos(r2) * (d2) + Math.cos(r2 - Math.PI * .5) * round_outer,
                Math.sin(r2) * (d2) + Math.sin(r2 - Math.PI * .5) * round_outer,
                
                Math.cos(r2) * (d2),
                Math.sin(r2) * (d2),
                
                Math.cos(r2) * (d2 - round_outer),
                Math.sin(r2) * (d2 - round_outer)
            );


            this.loadingGraphics.lineTo(
                Math.cos(r2) * (d1 + round_inner),
                Math.sin(r2) * (d1 + round_inner)
            );


            this.loadingGraphics.bezierCurveTo(
                Math.cos(r2) * (d1 + round_inner),
                Math.sin(r2) * (d1 + round_inner),

                Math.cos(r2) * (d1),
                Math.sin(r2) * (d1),

                Math.cos(r2) * (d1) + Math.cos(r2 - Math.PI * .5) * round_inner,
                Math.sin(r2) * (d1) + Math.sin(r2 - Math.PI * .5) * round_inner
            );


            this.loadingGraphics.lineTo(
                Math.cos(r1) * (d1) + Math.cos(r1 + Math.PI * .5) * round_inner,
                Math.sin(r1) * (d1) + Math.sin(r1 + Math.PI * .5) * round_inner
            );


            this.loadingGraphics.bezierCurveTo(
                Math.cos(r1) * (d1) + Math.cos(r1 + Math.PI * .5) * round_inner,
                Math.sin(r1) * (d1) + Math.sin(r1 + Math.PI * .5) * round_inner,

                Math.cos(r1) * (d1),
                Math.sin(r1) * (d1),

                Math.cos(r1) * (d1 + round_inner),
                Math.sin(r1) * (d1 + round_inner)
            );


            this.loadingGraphics.closePath();

            this.loadingGraphics.endFill();
        }

        this.loadingGraphics.beginFill(0xffffff, 0);
        this.loadingGraphics.lineStyle({
            color: 0x000000,
            width: 1,
            alpha: .5
        });

        this.stage.addChild(this.loadingGraphics);
        this.loadingGraphics.position.set(
            this.view.width * .5,
            this.view.height * .5
        );

        this.rotateLoadingGraphics();
    }


    rotateLoadingGraphics()
    {
        this.loadingGraphics.rotation += Math.PI * 2 / this.loadingGraphicsConfig.bars;
        gsap.delayedCall(this.loadingGraphicsConfig.rotateDelay, this.rotateLoadingGraphics);
    }


    destroyLoadingScreen()
    {
        gsap.killTweensOf(this.rotateLoadingGraphics);
        this.loadingGraphics.destroy();
    }


    onLoadComplete(loader: PIXI.Loader, resources: Record<string, PIXI.ILoaderResource>)
    {
        for (const id in resources)
        {
            const resource = resources[id];
            if (resource.type == PIXI.LoaderResource.TYPE.JSON && resource.spritesheet)
            {
                for (const tex_name in resource.spritesheet.textures)
                {
                    Resources.textures[tex_name] = resource.spritesheet.textures[tex_name];
                }

                for (const tex_name in resource.spritesheet.animations)
                {
                    Resources.animations[tex_name] = resource.spritesheet.animations[tex_name];
                }
            }
            else if (resource.loadType == PIXI.LoaderResource.LOAD_TYPE.XHR && sounds.indexOf(resource.extension) !== -1)
            {
                Resources.sounds[resource.name] = URL.createObjectURL(resource.data);
            }
        }

        this.destroyLoadingScreen();

        this.onLoad.call(this.onLoadScope);
    }


    destroy()
    {
        for (const id in this.loader.resources)
        {
            const resource = this.loader.resources[id];
            
            if (resource.type == PIXI.LoaderResource.TYPE.IMAGE && resource.texture)
            {
                resource.texture.destroy(true);
            }
            else if (resource.type == PIXI.LoaderResource.TYPE.JSON && resource.textures)
            {
                for (const id in resource.textures)
                {
                    resource.textures[id].destroy(true);
                }
            }

            delete this.loader.resources[id];
        }
        this.loader.destroy();
        super.destroy();
    }


}